import React, { useEffect, useState } from "react";
import { setGlobalLoading } from "../context/LoaderController";
import { Link } from "react-router-dom";
import { fetchStateSchedule, fetchReservedDistricts } from "../services/api";
import "../App.css";
import {
  LOCALSTORAGE_SELECTED_STATE_CODE,
  LOCALSTORAGE_SELECTED_STATE_NAME,
  ROUTE_HOME,
  PAGE_TITLE,
  NO_STATE_SELECTED,
  ERROR_FETCH_SCHEDULE,
  NO_SCHEDULE_FOUND,
  LOADING_SCHEDULE,
  NO_RECORDS_FOUND,
  EVENT_NAME_LABEL,
  START_DATE_LABEL,
  END_DATE_LABEL,
} from "../components/constants";

/* ======================================================
   DATE UTILS
====================================================== */
const parseDate = (str) => {
  if (!str) return null;
  const s = str.trim();

  const human = s.match(
    /^(\d{1,2})\s([A-Za-z]{3}),\s?(\d{2})\s(\d{1,2}):(\d{2})(AM|PM)$/i
  );
  if (human) {
    let [, d, mon, yy, h, m, ap] = human;
    const months = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    };
    let hour = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (ap.toUpperCase() === "AM" && hour === 12) hour = 0;
    return new Date(2000 + parseInt(yy, 10), months[mon], d, hour, m);
  }

  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (str) => {
  const d = parseDate(str);
  if (!d) return str;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getDaysLeft = (str) => {
  const end = parseDate(str);
  if (!end) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
};

/* ======================================================
   SCHEDULE MARQUEE
====================================================== */
export function ScheduleMarquee({ schedule }) {
  const phases = [
    { title: "Nomination", icon: "📄", match: ["nomination"] },
    { title: "Membership", icon: "👥", match: ["membership", "sync", "payment"] },
    { title: "Results", icon: "🏁", match: ["result"] },
  ];

  const items = phases
    .map((phase) => {
      const related = schedule.filter((s) =>
        phase.match.some((k) =>
          s.eventName?.toLowerCase().includes(k)
        )
      );

      if (related.length === 0) return null;

      const startEvent =
        related.find((s) =>
          s.eventName?.toLowerCase().includes("start")
        ) || related[0];

      const endEvent =
        related.find((s) =>
          s.eventName?.toLowerCase().includes("end")
        ) || related[related.length - 1];

      const daysLeft = endEvent?.endDate
        ? getDaysLeft(endEvent.endDate)
        : null;

      if (daysLeft === null || daysLeft < 0) return null;

      return {
        title: phase.title,
        icon: phase.icon,
        startDate: startEvent?.startDate
          ? formatDate(startEvent.startDate)
          : startEvent?.endDate
          ? formatDate(startEvent.endDate)
          : null,
        endDate: endEvent?.endDate
          ? formatDate(endEvent.endDate)
          : null,
        urgent: daysLeft !== null && daysLeft <= 5,
      };
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div className="schedule-banner">
      <div className="schedule-banner-track">
        {items.map((item, i) => (
          <div
            key={i}
            className={`schedule-pill ${item.urgent ? "urgent" : ""}`}
          >
            <span className="pill-icon">{item.icon}</span>
            <div className="pill-content">
              <span className="pill-title">{item.title}</span>
              <div className="pill-dates">
                {item.startDate && (
                  <div className="pill-date-block">
                    <span className="pill-date-label">Start</span>
                    <span className="pill-date-value">
                      {item.startDate}
                    </span>
                  </div>
                )}
                {item.endDate && (
                  <div className="pill-date-block">
                    <span className="pill-date-label">End</span>
                    <span className="pill-date-value">
                      {item.endDate}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================================================
   DATE STATUS
====================================================== */
const getDateStatus = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return "normal";

  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 0) return "past";
  if (diffHours <= 72) return "urgent";
  return "normal";
};

/* ======================================================
   MAIN PAGE
====================================================== */
export default function StateSchedulePage() {
  const stateId = localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_CODE);
  const stateName =
    localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_NAME) || "";

  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reservedDistricts, setReservedDistricts] = useState([]);
  const [showNotice, setShowNotice] = useState(false);
  const [noticeRemark, setNoticeRemark] = useState("");

  useEffect(() => {
    if (!stateId) {
      setError(NO_STATE_SELECTED);
      setLoading(false);
      setGlobalLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setGlobalLoading(true);

        const res = await fetchStateSchedule(stateId);
        const reserved = await fetchReservedDistricts(stateId);

        if (!mounted) return;

        if (Array.isArray(res)) {
          const now = new Date();

        const noticeItem = res.find((item) =>
          item.eventName?.toLowerCase().includes("notice")
        );

        if (noticeItem) {
const start = parseDate(noticeItem.startDate);
const end = parseDate(noticeItem.endDate);

if (start && end) {
  const today = new Date();

  // remove time part
  today.setHours(0,0,0,0);
  start.setHours(0,0,0,0);
  end.setHours(23,59,59,999);

  if (today >= start && today <= end) {
    setShowNotice(true);
    setNoticeRemark(noticeItem.remark || "");
  } else {
    setShowNotice(false);
    setNoticeRemark("");
  }
}
        }
          const filteredSchedule = res.filter(
            (item) => item.eventName?.toUpperCase() !== "NOTICE"
          );

          setSchedule(filteredSchedule);
        } else {
          setSchedule([]);
        }

        setReservedDistricts(Array.isArray(reserved) ? reserved : []);

        if (!res || res.length === 0) setError(NO_SCHEDULE_FOUND);
      } catch {
        if (!mounted) return;
        setError(ERROR_FETCH_SCHEDULE);
      } finally {
        setLoading(false);
        setGlobalLoading(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [stateId]);

  if (!stateId) {
    return (
      <div className="main-container">
        <h2>{PAGE_TITLE}</h2>
        <p>
          Select a state on the <Link to={ROUTE_HOME}>Home</Link> page.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Show only when NOTICE active */}
      {showNotice && (
        <div className="hold-marquee">
          <div className="hold-marquee-track">
            🚫 {noticeRemark} 🚫
          </div>
        </div>
      )}

      <div className="main-container">
        <h2 className="section-title">{PAGE_TITLE}</h2>
        <span className="iyc-badge">
          State: <strong>{stateName || stateId}</strong>
        </span>

        {loading && <p>{LOADING_SCHEDULE}</p>}
        {error && <p className="error-text">❌ {error}</p>}

        {!loading && !error && schedule.length > 0 && (
          <div className="table-container">
            <table className="common-table">
              <thead>
                <tr>
                  <th>{EVENT_NAME_LABEL}</th>
                  <th>{START_DATE_LABEL}</th>
                  <th>{END_DATE_LABEL}</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((item, i) => (
                  <tr
                    key={i}
                    className={`schedule-row ${getDateStatus(item.endDate)}`}
                  >
                    <td>{item.eventName}</td>
                    <td>{formatDate(item.startDate)}</td>
                    <td>{formatDate(item.endDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && schedule.length === 0 && (
          <p>{NO_RECORDS_FOUND}</p>
        )}
      </div>
    </>
  );
}